import React, { useCallback, useEffect, useRef, useState } from 'react';

import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import {
  Container,
  AddressRow,
} from './styles';
import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';
import MapComponent from './MapComponent';

import { StoreState } from '../../../store/createStore';
import { routesEndpoints } from '../../../routes/endpoint';
import { UserAddresses } from '../../../store/modules/auth/types';
import { createOrUpdateAddressCustomerCard } from '../../../store/modules/checkout/actions';
import { addressesCreatedOrUpdateRequest, setIsCategory, updateCoordinatesRequest } from '../../../store/modules/parameter/actions';
import getValidationErrors from '../../../utils/getValidationErrors';
import { customerCEPClear, customerCEPRequest } from '../../../store/modules/auth/actions';
import Input from '../../Input';
import Button from '../../Button';
import { useTranslation } from '../../../hooks/useTranslation';
import Loading from '../../Loading';
import { config } from '../../../config';
import { ContentLoading } from '../styles';

interface AddressModalProps {
  onSetModal: (value: boolean) => void;
}

const AddressCreateOrUpdate: React.FC<AddressModalProps> = ({onSetModal}: AddressModalProps) => {
  const formRef = useRef<FormHandles>(null);
  const dispatch = useDispatch();
  const [postcode, setPostcode] = useState('');
  const [coordinates, setCoordinates] = useState({
    latitude: 0,
    longitude: 0,
  });
  const [confirmLocation, setConfirmLocation] = useState(false);
  
  const history = useHistory();
  const { addressEdit : address, loading: loadingAddress } = useSelector((state: StoreState) => state.parameter);
  const { user, loading } = useSelector((state: StoreState) => state.auth);
  const { checkout, loading : loadingCheckout } = useSelector(
    (state: StoreState) => state.checkout,
  );
  const { onTranslation: t } = useTranslation();

  const handleLocationChange = useCallback((lat: number, lng: number) => {
    setCoordinates({ latitude: lat, longitude: lng });
  }, []);

  useEffect(() => {
    if (user.addresses?.length && user.data.seller) {
      history.push(routesEndpoints.NEW_ACCOUNT);
    }
  }, [user, history]);

  const handleSubmit = useCallback(
    async (data: UserAddresses) => {
      try {
        formRef.current?.setErrors({});
        const schema = Yup.object().shape({
          name: Yup.string()
          .required(`${t('main.messages_yup.last_name_required')}`),
          postcode: Yup.string().transform((value: string) => {
            return value.split('_').join('')
          })
            .required(`${t('main.messages_yup.post_code_required')}`)
            .min(9, `${t('main.messages_yup.min')} 6 ${t('main.messages_yup.digits')}`)
            .max(9, `${t('main.messages_yup.max')} 6 ${t('main.messages_yup.digits')}`),
          address: Yup.string()
            .required(`${t('main.messages_yup.address_required')}`),
          number: Yup.string().required(`${t('main.messages_yup.number_required')}`),
          neighborhood: Yup.string().required(`${t('main.messages_yup.neighborhood_required')}`),
          city: Yup.string().required(`${t('main.messages_yup.city_required')}`),
          state: Yup.string().required(`${t('main.messages_yup.state_required')}`)
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        if (address?.id && address?.id > 0) {
          data.id = address.id;
        }
        data.default_address = true;

        if(user.data.seller === 1 && checkout.customer?.data.customer.id) {
          dispatch(createOrUpdateAddressCustomerCard({
            ...data,
            postcode: data.postcode?.split('_').join(''),
            ...(coordinates.latitude !== 0 && coordinates.longitude !== 0 ? {
              latitude: coordinates.latitude.toString(),
              longitude: coordinates.longitude.toString(),
            } : {}),
          }, checkout.customer.data.customer.id, 
            checkout.customer.data.customer.customer_id, `${t('main.messages_toast.create_address_sucess')}`));
          onSetModal(false);
        }
        else if (user.data.seller === 1 && !checkout.customer?.data.customer.id) {
          onSetModal(false);
        }
        else {
          dispatch(addressesCreatedOrUpdateRequest({
            ...data,
            ...(coordinates.latitude !== 0 && coordinates.longitude !== 0 ? {
              latitude: coordinates.latitude.toString(),
              longitude: coordinates.longitude.toString(),
            } : {}),
            postcode: data.postcode?.split('_').join('')
          }));
          if (address?.id && address?.id > 0) {
            onSetModal(false);
          } else {
            setConfirmLocation(true);
          }
        }
        // dispatch(addressesEditRequest({} as UserAddresses));
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          formRef.current?.setErrors(errors);
          //konsole.log('handleSubmit errors:', errors);
        }
      }
    },
    [dispatch, address, onSetModal, checkout.customer, user.data.seller, t, coordinates],
  );

  const handleCEPChange = useCallback((value : string) => {
    const v = value.split('_').join('');

    if (v.length === 9) {
      setPostcode(value);
    }
  }, []);

  useEffect(() => {
    dispatch(customerCEPClear());
    dispatch(setIsCategory(false));

    setTimeout(() => {
      if (postcode) dispatch(customerCEPRequest(postcode));
    }, 300);
  }, [dispatch, postcode]);

  useEffect(() => {
    if (confirmLocation && address?.address) {
      setCoordinates({
        latitude: parseFloat(address.latitude || '0'),
        longitude: parseFloat(address.longitude || '0'),
      });
      return;
    }
    
    if (formRef.current && address?.address) {
      //konsole.log('carrega...', address);
      formRef.current.setData({
        address_name: address.address_name,
        name: address.address_name ? address.address_name : '',
        address: address.address,
        postcode: address.postcode,
        number: address.number ? address.number : '',
        neighborhood: address?.neighborhood,
        complement: address.complement,
        state: address.state,
        city: address?.city,
        reference: address.reference ? address.reference : ''
      });

      if (address.latitude && address.longitude) {
        setCoordinates({
          latitude: parseFloat(address.latitude),
          longitude: parseFloat(address.longitude),
        });
      }
    }
    else {
      if (formRef.current) {
        formRef.current.setData({
          address_name: '',
          name: '',
          address: '',
          postcode: '',
          number: '',
          neighborhood: '',
          complement: '',
          state: '',
          city: '',
          reference: '',
        });
      }
      setCoordinates({ latitude: 0, longitude: 0 });
    }
  }, [address, confirmLocation]);

  if (confirmLocation && loadingAddress) {
    return <ContentLoading><Loading size={160} bShowText /></ContentLoading>
  } else if (confirmLocation && !loadingAddress) {
    return (<Container>
      <h3>Confirmar localização</h3>
      {config.isAllowedMaps && (coordinates.latitude !== 0 && coordinates.longitude !== 0) && (
            <MapComponent
                latitude={coordinates.latitude}
                longitude={coordinates.longitude}
                onLocationChange={handleLocationChange}
            />
          )}
      <AddressRow>
            <Button
              button={{ type: 'button' }}
              atributes={{
                buttonType: 'primary',
                textColor: '#fff',
                textSize: '30px',
                borderColor: '#dddddd',
              }}
              onClick={() => {
                setConfirmLocation(false);
                onSetModal(false);
                if (address?.longitude !== coordinates.longitude.toString() && address?.latitude!== coordinates.latitude.toString()) {
                  dispatch(updateCoordinatesRequest({
                    id: address?.id as number,
                    coordinates: {
                      longitude: coordinates.longitude.toString(),
                      latitude: coordinates.latitude.toString(),
                    },
                  }));
                }
              }}
            >
              {loading || loadingCheckout ? (
                  <Loading color={"var(--primary)"} loading={loading} size={30} />
              ) : (
                <>
                  Confirmar localização
                </>)}
            </Button>
          </AddressRow>
    </Container>)
  }

  return (
    <Container>
        <Form
          // initialData={{ postcode: '06422070' }}
          ref={formRef}
          onSubmit={handleSubmit}
        >
          <AddressRow>
            <Input
              mask="#####-###"
              name="postcode"
              textLabel={`${t('main.component_address_customer.address_create_or_update.input_post_code')}`}
              Border="1px solid var(--primary)"
              handledInputBlurExt={handleCEPChange}
              formRef={formRef}
              width="200px !important"
            />
          </AddressRow>
          <AddressRow>
            <Input
              name="name"
              textLabel={`${t('main.component_address_customer.address_create_or_update.input_name_address')}`}
              Border="1px solid var(--primary)"
              formRef={formRef}
            />
          </AddressRow>
          <AddressRow>
            <Input
              name="address"
              textLabel={`${t('main.component_address_customer.address_create_or_update.input_address')}`}
              Border="1px solid var(--primary)"
              formRef={formRef}
            />
          </AddressRow>
          <AddressRow bDirectionColumn>
            <Input name="number" textLabel={`${t('main.component_address_customer.address_create_or_update.input_number')}`} Border="1px solid var(--primary)" />
            <Input
              name="complement"
              textLabel={`${t('main.component_address_customer.address_create_or_update.input_complement')}`}
              Border="1px solid var(--primary)"
              formRef={formRef}
            />
          </AddressRow>
          <AddressRow>
            <Input 
              name="neighborhood" 
              textLabel={`${t('main.component_address_customer.address_create_or_update.input_neighborhood')}`} 
              Border="1px solid var(--primary)" 
              formRef={formRef}
            />
            <Input 
              name="reference" 
              textLabel="Referência"
              Border="1px solid var(--primary)" 
              formRef={formRef}
            />
          </AddressRow>
          <AddressRow bDirectionColumn>
            <Input 
              name="city" 
              textLabel={`${t('main.component_address_customer.address_create_or_update.input_city')}`} 
              Border="1px solid var(--primary)" 
              formRef={formRef}
            />
            <Input 
            name="state" 
            textLabel={`${t('main.component_address_customer.address_create_or_update.input_state')}`} 
            Border="1px solid var(--primary)" 
            formRef={formRef}
            />
          </AddressRow>
          
          {config.isAllowedMaps && (coordinates.latitude !== 0 && coordinates.longitude !== 0) && (
            <MapComponent
                latitude={coordinates.latitude}
                longitude={coordinates.longitude}
                onLocationChange={handleLocationChange}
            />
          )}
          
          <AddressRow>
            <Button
              button={{ type: 'submit' }}
              atributes={{
                buttonType: 'primary',
                textColor: '#fff',
                textSize: '30px',
                borderColor: '#dddddd',
              }}
            >
              {loading || loadingCheckout ? (
                  <Loading color={"var(--primary)"} loading={loading} size={30} />
              ) : (
                <>
                  {t('main.globals.save')}
                </>)}
            </Button>
          </AddressRow>
        </Form>
    </Container>
  );
}

export default AddressCreateOrUpdate;
